import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  formaPorId,
  percentualRecebido,
  recebidoAcimaDoContrato,
  saldoDoLead,
} from '@/leads/recebimentos';
import { ouvirRecebimentos, removerRecebimento } from '@/services/recebimentosService';

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const exibirData = (valor) => {
  const d = typeof valor?.toDate === 'function' ? valor.toDate() : valor;
  return d ? format(d, 'dd/MM/yy') : '—';
};

/**
 * Recebimentos do lead (VA): o que o paciente efetivamente pagou.
 *
 * Três números distintos na mesma tela, porque significam coisas diferentes:
 * TOTAL (a venda), ENTRADA (caixa do dia, paga no ato) e RECEBIDO (soma dos
 * recebimentos — a base da comissão). O SALDO desconta só o recebido: a entrada
 * volta na importação do Careflow como parcela liquidada, e descontá-la aqui
 * também abateria o mesmo dinheiro duas vezes.
 *
 * Lançar e remover são do GESTOR (as rules recusam os demais). O SDR vê tudo —
 * é o saldo do lead dele.
 */
export default function BlocoRecebimentos({ lead, clinicaId, podeLancar = false, config = null }) {
  const sistema = nomeDoSistema(config);
  const [recebimentos, setRecebimentos] = useState([]);
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);


  useEffect(() => {
    setRecebimentos([]);
    if (!lead?.id) return undefined;
    return ouvirRecebimentos(clinicaId, lead.id, setRecebimentos, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir recebimentos:', e);
      setErro('Não foi possível carregar os recebimentos.');
    });
  }, [clinicaId, lead?.id]);

  // Só faz sentido para quem tem contrato fechado.
  if (!(Number(lead?.valorFechado) > 0)) return null;

  const saldo = saldoDoLead(lead);
  const pct = percentualRecebido(lead);

  async function executar(acao) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      console.error('[OdontoSDR] Ação de recebimento falhou:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Só o gestor pode lançar ou remover recebimentos.'
          : (e.message ?? 'Não foi possível concluir a ação.'),
      );
    } finally {
      setOcupado(false);
    }
  }



  return (
    <section className="bloco-recebimentos">
      {/* ★★ WD-c2: o botão "Lançar" SAIU. Recebimento entra sempre pela PLANILHA (importação do {sistema}).
          ★ LIMITE, medido e declarado: `registrarRecebimento` é ESCRITA DIRETA do cliente (`writeBatch` em
          `services/recebimentosService.js`), não callable, e as rules permitem `create` ao GESTOR
          (`firestore.rules:196`). Tirar o botão daqui NÃO tira a capacidade — só tira desta tela. O gestor
          continua lançando pela página `/recebimentos` (`RecebimentosMesPage`), que é o ritual mensal dele.
          Pendência registrada em `docs/wd-testes.md`. */}
      <div className="bloco-titulo">
        <h3>Recebimentos</h3>
      </div>

      {/* ★ WD-c1: "Total do tratamento" e "Entrada no ato" SAÍRAM daqui. O total tinha DOIS rótulos na mesma
          tela para o mesmo número (`valorFechado`), e a entrada era editável num lugar onde ninguém
          procurava. Os dois vivem agora no bloco "Negociação e fechamento", com um rótulo só cada. */}
      <dl className="dados-lead">
        <dt>Recebido</dt>
        <dd>{moeda(lead.valorRecebido)}</dd>
        {/* ★★ WD-c2 (item 4) — "Saldo" virou "VALOR A PAGAR". "Saldo" é palavra de contabilidade e a
            clínica lê ao contrário (saldo POSITIVO soa a dinheiro em caixa, quando aqui é dívida do
            paciente). O número não mudou: continua total − recebido (`saldoDoLead`). */}
        <dt>Valor a pagar</dt>
        <dd className={saldo > 0 ? 'saldo-aberto' : 'valor'}>{moeda(saldo)}</dd>
      </dl>

      <div className="barra" title={`${pct.toFixed(0)}% recebido`}>
        <div className="barra-preenchida" style={{ width: `${pct}%`, background: '#16a34a' }} />
      </div>
      {recebidoAcimaDoContrato(lead) && (
        <p className="indicador-nota">Recebido acima do contrato — confira o valor total.</p>
      )}

      {erro && <p className="erro">{erro}</p>}

      {/* ★ WD-c1: a caixinha "Dados do {sistema}" (código, nome e entrada) saiu daqui e subiu para o bloco
          "Negociação e fechamento" (`CamposFechamento.jsx`). Ela gravava `valorEntrada` com
          `registrarNaTimeline: false` num canto do bloco de recebimentos — o bypass registrado no §2 de
          `docs/wd-testes.md`. Ao subir, o bypass deixou de existir: há UM lugar de editar cada campo. */}

      <ul className="lista-recebimentos">
        {recebimentos.map((r) => (
          <li key={r.id}>
            <div>
              <strong>{moeda(r.valor)}</strong>
              <span className="indicador-nota">
                {exibirData(r.data)} · {formaPorId(r.forma).label}
                {r.fonte === 'importacao' && <span className="pilula-inferido">importado</span>}
              </span>
              {r.observacao && <p className="campo-ajuda">{r.observacao}</p>}
            </div>
            {podeLancar && (
              <button
                type="button"
                className="btn-texto perigo"
                disabled={ocupado}
                title="Remover este recebimento"
                onClick={() => executar(() => removerRecebimento(clinicaId, lead, r, recebimentos))}
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>
      {recebimentos.length === 0 && (
        <p className="vazio compacto">
          Nenhum recebimento. Eles chegam pela importação da planilha do {sistema}.
        </p>
      )}
    </section>
  );
}
